import React from 'react';
import { Platform } from 'react-native';

import githubReposData from '@/data/github-repos.json';

interface GithubCardProps {
  repo: string; // format: "owner/repo"
  path?: string; // optional path within the repo (e.g. "src/animations/16-smooth-dropdown")
}

interface RepoData {
  name: string;
  description: string;
  stargazers_count: number;
  forks_count: number;
  language: string;
  html_url: string;
  owner: {
    avatar_url: string;
    login: string;
  };
}

const precomputedRepos = githubReposData as Record<string, RepoData>;

function StarIcon() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 16 16"
      fill="currentColor"
      className="w-4 h-4"
    >
      <path d="M8 .25a.75.75 0 0 1 .673.418l1.882 3.815 4.21.612a.75.75 0 0 1 .416 1.279l-3.046 2.97.719 4.192a.75.75 0 0 1-1.088.791L8 12.347l-3.766 1.98a.75.75 0 0 1-1.088-.79l.72-4.194L.818 6.374a.75.75 0 0 1 .416-1.28l4.21-.611L7.327.668A.75.75 0 0 1 8 .25Z" />
    </svg>
  );
}

function ForkIcon() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 16 16"
      fill="currentColor"
      className="w-4 h-4"
    >
      <path d="M5 5.372v.878c0 .414.336.75.75.75h4.5a.75.75 0 0 0 .75-.75v-.878a2.25 2.25 0 1 1 1.5 0v.878a2.25 2.25 0 0 1-2.25 2.25h-1.5v2.128a2.251 2.251 0 1 1-1.5 0V8.5h-1.5A2.25 2.25 0 0 1 3.5 6.25v-.878a2.25 2.25 0 1 1 1.5 0ZM5 3.25a.75.75 0 1 0-1.5 0 .75.75 0 0 0 1.5 0Zm6.75.75a.75.75 0 1 0 0-1.5.75.75 0 0 0 0 1.5Zm-3 8.75a.75.75 0 1 0-1.5 0 .75.75 0 0 0 1.5 0Z" />
    </svg>
  );
}

function GithubIcon() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="currentColor"
      className="w-5 h-5 text-white"
    >
      <path d="M12 0C5.374 0 0 5.373 0 12c0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23A11.509 11.509 0 0 1 12 5.803c1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576C20.566 21.797 24 17.3 24 12c0-6.627-5.373-12-12-12z" />
    </svg>
  );
}

function ChevronIcon() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 20 20"
      fill="currentColor"
      className="w-3 h-3 text-text-tertiary mx-1"
    >
      <path
        fillRule="evenodd"
        d="M8.22 5.22a.75.75 0 0 1 1.06 0l4.25 4.25a.75.75 0 0 1 0 1.06l-4.25 4.25a.75.75 0 0 1-1.06-1.06L11.94 10 8.22 6.28a.75.75 0 0 1 0-1.06Z"
        clipRule="evenodd"
      />
    </svg>
  );
}

function PathDisplay({ repoName, path }: { repoName: string; path: string }) {
  const segments = path.split('/');
  const lastSegment = segments.pop();

  return (
    <span className="flex items-center">
      <span className="text-text-tertiary">{repoName}</span>
      <ChevronIcon />
      <span className="text-text-primary font-semibold">{lastSegment}</span>
    </span>
  );
}

function formatNumber(num: number): string {
  if (num >= 1000) {
    return (num / 1000).toFixed(1).replace(/\.0$/, '') + 'k';
  }
  return num.toString();
}

export function GithubCard({ repo, path }: GithubCardProps) {
  if (Platform.OS !== 'web') {
    return null;
  }

  const repoData = precomputedRepos[repo];

  const repoUrl = path
    ? `https://github.com/${repo}/tree/main/${path}`
    : `https://github.com/${repo}`;

  // Fallback for repos not in precomputed data
  if (!repoData) {
    return (
      <a
        href={repoUrl}
        target="_blank"
        rel="noopener noreferrer"
        className="block mb-6 no-underline"
      >
        <div className="rounded-lg border border-border-muted bg-surface-elevated p-4 hover:border-border transition-colors">
          <div className="flex items-center gap-2 text-text-primary">
            <GithubIcon />
            <span className="font-medium">{repo}</span>
          </div>
        </div>
      </a>
    );
  }

  const targetUrl = path
    ? `${repoData.html_url}/tree/main/${path}`
    : repoData.html_url;

  return (
    <a
      href={targetUrl}
      target="_blank"
      rel="noopener noreferrer"
      className="block mb-6 no-underline hover:no-underline"
    >
      <div className="rounded-lg border border-border-muted bg-surface-elevated p-4 hover:border-border transition-all">
        <div className="flex items-start gap-3">
          <img
            src={repoData.owner.avatar_url}
            alt={repoData.owner.login}
            className="w-10 h-10 rounded-full"
          />
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <GithubIcon />
              {path ? (
                <PathDisplay repoName={repoData.name} path={path} />
              ) : (
                <span className="font-semibold text-text-primary truncate">
                  {repoData.owner.login}/{repoData.name}
                </span>
              )}
            </div>
            {repoData.description && (
              <p className="mt-1 text-sm text-text-secondary line-clamp-2">
                {repoData.description}
              </p>
            )}
            <div className="flex items-center gap-4 mt-3 text-xs text-text-tertiary">
              {repoData.language && (
                <span className="flex items-center gap-1">
                  <span className="w-3 h-3 rounded-full bg-warning" />
                  {repoData.language}
                </span>
              )}
              <span className="flex items-center gap-1">
                <StarIcon />
                {formatNumber(repoData.stargazers_count)}
              </span>
              <span className="flex items-center gap-1">
                <ForkIcon />
                {formatNumber(repoData.forks_count)}
              </span>
            </div>
          </div>
        </div>
      </div>
    </a>
  );
}
